/**
 * Cloudflare Pages iframe entry point.
 *
 * The merchant SDK loads routes such as /fields/card and /sandbox/fields/cardNumber.
 * This entry resolves the requested field from the route, validates the embedding
 * merchant origin, and starts the secure input renderer.
 */
import { InputRenderer } from "./InputRenderer";

const ROOT_ID = "secure-field-root";
const VALID_ELEMENT_TYPES = new Set(["card", "cardNumber", "cardExpiry", "cardCvc"]);

function start(): void {
  const root = document.getElementById(ROOT_ID);
  if (!root) {
    throw new Error(`Missing #${ROOT_ID} container.`);
  }

  const elementType = resolveElementType(window.location);
  if (!elementType) {
    renderError(root);
    return;
  }

  const merchantOrigin = resolveMerchantOrigin(window.location, document.referrer);
  if (!merchantOrigin) {
    renderError(root);
    return;
  }

  const renderer = new InputRenderer({ merchantOrigin, elementType });
  renderer.init(root);
  window.addEventListener("pagehide", () => renderer.destroy(), { once: true });
}

export function resolveElementType(location: Location): string | null {
  const queryElementType = location.search
    ? new URLSearchParams(location.search).get("elementType")
    : null;
  if (queryElementType && VALID_ELEMENT_TYPES.has(queryElementType)) {
    return queryElementType;
  }

  const segments = location.pathname.split("/").filter(Boolean);
  const fieldsIndex = segments.lastIndexOf("fields");
  const pathElementType =
    fieldsIndex === -1
      ? segments[segments.length - 1]
      : segments[fieldsIndex + 1];

  return pathElementType && VALID_ELEMENT_TYPES.has(pathElementType)
    ? pathElementType
    : null;
}

export function resolveMerchantOrigin(
  location: Location,
  referrer: string
): string | null {
  const queryOrigin = location.search
    ? new URLSearchParams(location.search).get("merchantOrigin")
    : null;
  const originFromQuery = toHttpOrigin(queryOrigin);
  if (originFromQuery) return originFromQuery;

  const originFromReferrer = toHttpOrigin(referrer);
  if (originFromReferrer) return originFromReferrer;

  const ancestorOrigins = location.ancestorOrigins;
  const originFromAncestor =
    ancestorOrigins && ancestorOrigins.length > 0
      ? toHttpOrigin(ancestorOrigins[0])
      : null;

  return originFromAncestor;
}

function toHttpOrigin(value: string | null | undefined): string | null {
  if (!value) return null;

  try {
    const url = new URL(value);
    return url.protocol === "https:" || url.protocol === "http:"
      ? url.origin
      : null;
  } catch {
    return null;
  }
}

function renderError(root: HTMLElement): void {
  root.setAttribute("role", "alert");
  root.textContent = "Secure field unavailable.";
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", start, { once: true });
} else {
  start();
}
